// v195 admin API client for OIDC trust-policy management. Backs the
// AdminOIDCPolicies page. The token-exchange endpoint itself
// (/api/oidc/exchange/gitlab) is not invoked from the SPA — it's the
// CI-runner-facing entry-point, only authenticated via the inbound
// JWT — so this file deliberately omits an `exchangeGitlabToken`
// helper to discourage SPA callers from feeding their own session
// cookies through the exchange path.

import useSWR from 'swr';

export interface OIDCPolicy {
  id: string;
  project_id: string;
  name: string;
  issuer: string;
  audience: string;
  subject_pattern: string;
  claim_filters: Record<string, unknown>;
  grants_role: 'reader' | 'publisher' | 'maintainer';
  grants_repos: string[];
  service_account_id?: string | null;
  default_ttl_seconds: number;
  max_ttl_seconds: number;
  enabled: boolean;
  created_at: string;
  created_by?: string;
}

export interface OIDCPolicyInput {
  project_id: string;
  name: string;
  issuer: string;
  audience: string;
  subject_pattern: string;
  claim_filters?: Record<string, unknown>;
  grants_role: 'reader' | 'publisher' | 'maintainer';
  grants_repos?: string[];
  service_account_id?: string | null;
  default_ttl_seconds?: number;
  max_ttl_seconds?: number;
  enabled?: boolean;
}

export interface OIDCTestMatchResponse {
  matched: boolean;
  reason?: string;
  policy_id?: string;
  policy_name?: string;
  project_id?: string;
  scopes?: string[];
  expires_in_seconds?: number;
  resolved_service_account?: string;
  service_account_resolution_error?: string;
  sub?: string;
}

interface ListResponse {
  policies: OIDCPolicy[];
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useOIDCPolicies(projectId?: string) {
  const url = projectId
    ? `/api/admin/oidc/policies?project_id=${encodeURIComponent(projectId)}`
    : '/api/admin/oidc/policies';
  return useSWR<ListResponse>(url, json);
}

export async function createOIDCPolicy(
  input: OIDCPolicyInput,
): Promise<{ id: string; created_at: string }> {
  const res = await fetch('/api/admin/oidc/policies', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

export async function updateOIDCPolicy(
  id: string,
  input: OIDCPolicyInput,
): Promise<void> {
  const res = await fetch(`/api/admin/oidc/policies/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function deleteOIDCPolicy(id: string): Promise<void> {
  const res = await fetch(`/api/admin/oidc/policies/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204)
    throw new Error(`${res.status}: ${await res.text()}`);
}

// testOIDCToken pastes a JWT into the dry-run endpoint and returns
// which policy it would match (or the closest reason for failure).
// The endpoint deliberately skips signature verification so operators
// can debug subject_pattern + claim_filters with expired or scratch
// IdP tokens.
export async function testOIDCToken(idToken: string): Promise<OIDCTestMatchResponse> {
  const res = await fetch('/api/admin/oidc/policies/test', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}
